import { PendingItemDto } from '@act/shared/data-transfer-objects';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { loadPendingItems } from 'redux/slices/feedSlice';
import {
  PendingItemFilters,
  filterChoices,
  usePendingItemSelector,
} from 'redux/utils/selectors/pending-item-selectors';
import { FilterChips } from '@act/storybook';
import { FilterDiv, ScrollableStyled } from './feed-styles';
import SinglePendingItem from './single-pending-item';

export interface PendingItemsProps {
  memberId: string;
}

const PendingItems = ({ memberId }: PendingItemsProps) => {
  const [filters, setFilters] = useState<PendingItemFilters[]>([]);
  const [searchParams] = useSearchParams();
  const feedItemExternalType = searchParams.get('feedItemExternalType');
  const feedItemExternalId = searchParams.get('feedItemExternalId');
  const pendingItems: PendingItemDto[] =
    usePendingItemSelector.pendingItemsForMember(memberId, filters);
  const [itemRefs, setItemRefs] = useState<{
    [key: string]: React.RefObject<HTMLDivElement>;
  }>({});
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (memberId) {
      dispatch(loadPendingItems({ userId: memberId }));
    }
  }, [memberId, dispatch]);

  // Create refs for each pending item
  useEffect(() => {
    setItemRefs(prev => {
      const newRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
      let hasNewRefs = false;
      pendingItems.forEach(item => {
        const key = `${item.externalItemType}-${item.externalItemId}`;
        if (!prev[key]) {
          newRefs[key] = React.createRef<HTMLDivElement>();
          hasNewRefs = true;
        }
      });
      return hasNewRefs ? { ...prev, ...newRefs } : prev;
    });
  }, [pendingItems]);

  // Scroll to the target item when query params are present
  useEffect(() => {
    if (
      feedItemExternalType &&
      feedItemExternalId &&
      Object.keys(itemRefs).length > 0 &&
      pendingItems.length > 0
    ) {
      const targetKey = `${feedItemExternalType}-${feedItemExternalId}`;
      const targetRef = itemRefs[targetKey];

      if (targetRef?.current) {
        setTimeout(() => {
          targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedKey(targetKey);
          setTimeout(() => {
            setHighlightedKey(null);
          }, 3500);
        }, 300);
      }
    }
  }, [feedItemExternalType, feedItemExternalId, itemRefs, pendingItems.length]);

  return (
    <ScrollableStyled ref={scrollContainerRef}>
      <div className="scroll-content">
        <FilterDiv>
          <FilterChips
            filterChoices={filterChoices}
            setSelectedFilters={setFilters}
          />
        </FilterDiv>
        {pendingItems.map(item => {
          const key = `${item.externalItemType}-${item.externalItemId}`;
          const ref = itemRefs[key];
          return (
            <div key={key} ref={ref}>
              <SinglePendingItem
                item={item}
                shouldHighlight={key === highlightedKey}
              />
            </div>
          );
        })}
      </div>
    </ScrollableStyled>
  );
};

export default PendingItems;
